import { isDefined } from "@/utils/utils";
import t from "../../../../app/i18n";
import { CreateUpdateContractClient, LegalClient, NaturalClient } from "../../../client/types";
import { createUpdateContractClientToClient } from "../../../client/utils";
import { RealtyCalcDraft } from "../../drafts/types";
import { CalcType, ClientExperience } from "../../enums";
import { BuildingType, NearbyBuildingType } from "./enums";
import { RealtyCalc, RealtyCalcGeneralBuildingData, RealtyFormClients, RealtyGen, RealtyGenForm } from "./types";

export const createRealtyFormClientsObjectFromCalcData = (calcData: RealtyCalc): RealtyFormClients => ({
  policyHolder: isDefined(calcData.clientsData?.policyHolderIndex)
    ? createUpdateContractClientToClient(
        calcData.clientsData.clients?.[calcData.clientsData?.policyHolderIndex] as CreateUpdateContractClient,
      )
    : undefined,
  insured: undefined,
  representative: undefined,
  vinculation: undefined,
});

export const createRealtyFormClientsObjectFromGenData = (
  genData: RealtyGen | RealtyCalcDraft["draftData"],
): RealtyFormClients => ({
  policyHolder: isDefined(genData.clientsData.policyHolderIndex)
    ? createUpdateContractClientToClient(
        genData.clientsData.clients?.[genData.clientsData.policyHolderIndex] as CreateUpdateContractClient,
      )
    : undefined,
  insured:
    genData.clientsData.insuredClientIndex !== genData.clientsData.policyHolderIndex
      ? (createUpdateContractClientToClient(
          genData.clientsData.clients?.[genData.clientsData.insuredClientIndex] as CreateUpdateContractClient,
        ) as NaturalClient)
      : undefined,
  representative: isDefined(genData.clientsData.representativeIndex)
    ? (createUpdateContractClientToClient(
        genData.clientsData.clients?.[genData.clientsData.representativeIndex] as CreateUpdateContractClient,
      ) as NaturalClient)
    : undefined,
  vinculation: isDefined(genData.clientsData.vinculationClientIndex)
    ? (createUpdateContractClientToClient(
        genData.clientsData.clients?.[genData.clientsData.vinculationClientIndex] as CreateUpdateContractClient,
      ) as LegalClient)
    : undefined,
});

type DamagesAndFloods = Pick<
  RealtyCalcGeneralBuildingData,
  | "numberOfDamagesInLastThreeYears"
  | "numberOfDamagesInLastFiveYears"
  | "numberOfDamagesInLastTenYears"
  | "numberOfFloodsInLastTenYears"
>;

const fillMissingDamagesAndFloods = <T extends DamagesAndFloods>(generalBuildingData: T): T => {
  const storedData = generalBuildingData as Partial<DamagesAndFloods>;

  const damagesInThreeYears = storedData.numberOfDamagesInLastThreeYears ?? 0;
  const damagesInFiveYears = storedData.numberOfDamagesInLastFiveYears ?? damagesInThreeYears;
  const damagesInTenYears = storedData.numberOfDamagesInLastTenYears ?? damagesInFiveYears;

  return {
    ...generalBuildingData,
    numberOfDamagesInLastThreeYears: damagesInThreeYears,
    numberOfDamagesInLastFiveYears: damagesInFiveYears,
    numberOfDamagesInLastTenYears: damagesInTenYears,
    numberOfFloodsInLastTenYears: storedData.numberOfFloodsInLastTenYears ?? 0,
  };
};

export const createRealtyCalcObjectFromCalcData = (calcData: RealtyCalc, clients: RealtyFormClients): RealtyCalc => ({
  ...calcData,
  generalBuildingData: fillMissingDamagesAndFloods(calcData.generalBuildingData),
  clientsData: {
    ...calcData.clientsData,
    policyHolderIdentifier: clients.policyHolder?.identifier,
  },
  generalInsuranceData: {
    ...calcData.generalInsuranceData,
    effectiveBeginningDate: calcData.generalInsuranceData.effectiveBeginningDate,
  },
});

export const createRealtyCalcObjectFromGenData = (
  genData: RealtyGen | RealtyCalcDraft["draftData"],
  clients: RealtyFormClients,
): RealtyCalc => ({
  type: CalcType.REALTY,
  clientsData: {
    policyHolderIdentifier: clients.policyHolder?.identifier,
  },
  generalBuildingData: fillMissingDamagesAndFloods(genData.generalBuildingData),
  houseOrRecreationalBuildingData: genData.houseOrRecreationalBuildingData,
  apartmentData: genData.apartmentData,
  realtyInsuranceData: genData.realtyInsuranceData,
  householdInsuranceData: genData.householdInsuranceData,
  liabilityInsuranceData: genData.liabilityInsuranceData,
  generalInsuranceData: {
    ...genData.generalInsuranceData,
    effectiveBeginningDate: genData.generalInsuranceData.effectiveBeginningDate,
  },
});

export const createRealtyGenFormDataObject = (
  genData: RealtyGen | RealtyCalcDraft["draftData"],
  clients: RealtyFormClients,
): RealtyGenForm => ({
  clientsData: {
    policyHolderIsAlsoInsured: genData.clientsData.policyHolderIndex === genData.clientsData.insuredClientIndex,
    policyHolderEmail: genData.clientsData.policyHolderEmail,
    policyHolderPhone: genData.clientsData.policyHolderPhone,
    policyHolderMarketingConsent: genData.clientsData.policyHolderMarketingConsent,
    policyHolderIdentityCardNumber: (clients.policyHolder as NaturalClient)?.identityCardNumber,
    insuredClientIdentifier: clients.insured?.identifier,
    representativeIdentifier: clients.representative?.identifier,
    representativeFunction: genData.clientsData.representativeFunction,
    vinculationClientIdentifier: clients.vinculation?.identifier,
  },
  generalBuildingData: { locationOtherInfo: genData.generalBuildingData.locationOtherInfo },
  apartmentData: genData.apartmentData ? { numberOfFloors: genData.apartmentData.numberOfFloors } : undefined,
  realtyInsuranceData: genData.realtyInsuranceData
    ? { garageReinsurance: genData.realtyInsuranceData.garageReinsurance }
    : undefined,
  householdInsuranceData: genData.householdInsuranceData
    ? { equipmentReinsurances: genData.householdInsuranceData.equipmentReinsurances || [] }
    : undefined,
  generalInsuranceData: {
    signCity: genData.generalInsuranceData.signCity ?? "",
    anotherInsuranceCompanyId: genData.generalInsuranceData.anotherInsuranceCompanyId,
    anotherInsuranceCompanyEnabled: !!genData.generalInsuranceData.anotherInsuranceCompanyId,
    photovoltaicPower: genData.generalInsuranceData.photovoltaicPower,
    photovoltaicPurchasePrice: genData.generalInsuranceData.photovoltaicPurchasePrice,
    photovoltaicIncluded:
      !!genData.generalInsuranceData.photovoltaicPower && !!genData.generalInsuranceData.photovoltaicPurchasePrice,
    inspectionExecuted: !!genData.generalInsuranceData.inspectionExecuted,
    agentIsClosePersonToClient: !!genData.generalInsuranceData.agentIsClosePersonToClient,
    loanNumber: genData.generalInsuranceData.loanNumber,
    crossSelling: {
      unionHealthContractNumber: genData.generalInsuranceData.crossSelling?.unionHealthContractNumber,
    },
  },
  financialMediationData: genData.financialMediationData ?? {
    clientExperience: "" as ClientExperience,
    ownedByClient: true,
  },
});

export const NEARBY_BUILDING_TYPES_WITH_AREA = [
  NearbyBuildingType.DETACHED_GARAGE,
  NearbyBuildingType.GARDEN,
  NearbyBuildingType.FARM_BUILDING,
  NearbyBuildingType.WORKSHOP,
  NearbyBuildingType.ARBOUR,
  NearbyBuildingType.GARDEN_HOUSE,
  NearbyBuildingType.WINTER_GARDEN,
  NearbyBuildingType.GREENHOUSE,
  NearbyBuildingType.UNDERGROUND_CELLAR,
  NearbyBuildingType.SAUNA,
  NearbyBuildingType.TENNIS_COURT,
  NearbyBuildingType.PAVEMENT,
  NearbyBuildingType.OTHER,
];

export const NEARBY_BUILDING_TYPES_WITH_VOLUME = [
  NearbyBuildingType.BUILT_SWIMMING_POOL,
  NearbyBuildingType.BUILT_SWIMMING_POOL_WITH_ROOF,
];

export const realtyCalcPageTitle = (
  buildingType: BuildingType,
  realtyInsuranceEnabled?: boolean,
  householdInsuranceEnabled?: boolean,
): string => {
  let pageTitle;

  switch (buildingType) {
    case BuildingType.HOUSE:
      pageTitle = t("calc.realty.titles.house");
      break;
    case BuildingType.APARTMENT:
      pageTitle = t("calc.realty.titles.apartment");
      break;
    case BuildingType.RECREATIONAL_BUILDING:
      pageTitle = t("calc.realty.titles.recreationalBuilding");
      break;
    default:
      return t("calc.realty.helpers.noBuildingType");
  }

  const insurancesTranslations: string[] = [];
  if (realtyInsuranceEnabled) {
    insurancesTranslations.push(t("calc.realty.sections.realtyInsurance"));
  }

  if (householdInsuranceEnabled) {
    insurancesTranslations.push(t("calc.realty.sections.householdInsurance"));
  }

  return insurancesTranslations.length === 0 ? pageTitle : `${pageTitle} (${insurancesTranslations.join(", ")})`;
};
